/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  DictionaryItemDto,
  DictionaryRestService,
  GlobalDictionaryDto,
} from '@gh/common-shared-data';
import { REGION_CODE } from '@gh/config';
import { createEnum, Enum, EnumSet } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { of$, throwError$ } from '@gh/rx';
import { catchError$, map$, switchMap$, tap$, filter$, first$ } from '@gh/rx/operators';

import { isNil } from 'lodash';
import { Observable, Subject } from 'rxjs';

import {
  Dictionary,
  DictionaryItem,
  dictionaryItemFromDto,
  EnumDictionary,
  TextDictionary,
} from './dictionary';
import { DictionaryContext } from './dictionary-context.service';
import { UserContext } from '../user';

export type DictionaryParams = Hash<any>;
export const globalDictionaries = {
  gender: 'GENDER',
  states: 'STATES',
  stage: 'STAGE',
  referredBy: 'REFERRED_BY',
  priority: 'PRIORITY',
  paidBy: 'PAID_BY',
  payBasis: 'PAY_BASIS',
  employeePayroll: 'PAYROLL_CYCLE',
  notProceedingReasons: 'NOT_PROCEEDING_REASON',
  jobNotProceedingReasons: 'JOB_NOT_PROCEEDING_REASON',
  activityType: 'ACTIVITY_TYPE',
  MilestoneEvents: 'MILESTONE_EVENTS',
  LandAreaUom: 'LAND_AREA_UOM',
};

export interface DictionaryDefinition<D extends Dictionary<any>> {
  get(dictionaryService: DictionaryService, params?: DictionaryParams): Observable<D>;

  filter(dictionary: D, params?: DictionaryParams): D;
}

export type DictionaryDefinitionPredicate<T> = (item: DictionaryItem<T>, i?: number, array?: DictionaryItem<T>,
  params?: DictionaryParams) => boolean;
interface GlobalDictionary {
  time: number;
  dictionaries: {
    [key: string]: DictionaryItemDto[];
  };
  locale: string;
}

@Injectable()
export class DictionaryService {
  private enumCache: Hash<EnumDictionary<any>> = {};
  private cacheTime = 7200000; // 2h
  private isDictionaryLoading: boolean;
  private onSubject = new Subject<string>();
  private refetchedMissingCodes = new Set<string>();

  constructor(private dictionaryDataService: DictionaryRestService,
    private dictionaryContext: DictionaryContext,
    private translationService: TranslationService,
    private userContext: UserContext) {

  }

  get(code: string): Observable<TextDictionary> {
    return this.getTextDictionaryFromGlobal(code).pipe(catchError$(() => this.getSeparateDictionary(code)));
  }

  getByEnumGlobal(name: string): Observable<EnumDictionary<EnumSet<any>>> {
    return this.getDictionaryFromGlobal(name).pipe(switchMap$((res) => {
      const resEnum = this.getEnumByDictionary(name, res);
      return this.getByEnum(resEnum, !!res[0].localization);
    }));
  }

  getByEnum<T extends Enum<S>, S>(enumeration: Enum<T>,
    notNeedTranslate?: boolean): Observable<EnumDictionary<EnumSet<T>>> {
    const name = Enum.name(enumeration);
    const dictionary = this.enumCache[name];

    if (!isNil(dictionary)) {
      return of$(dictionary);
    }

    if (notNeedTranslate) {
      const values: any[] = [];
      for (const key in enumeration) {
        if (Object.prototype.hasOwnProperty.call(enumeration, key)) {
          values.push({
            id: key,
            name: enumeration[key],
          });
        }
      }
      const newDictionary = new EnumDictionary(<Enum<any>>enumeration, values);
      return of$(this.enumCache[name] = newDictionary as any);
    }

    return this.getRawData(name).pipe(
      map$((values) => new EnumDictionary(<Enum<any>>enumeration, values.map((item) => ({
        id: Enum.byValue(enumeration, item.id),
        name: item.name,
      })))),
      tap$((newDictionary) => this.enumCache[name] = newDictionary));
  }

  getByDef<D extends Dictionary<any>>(definition: DictionaryDefinition<D>, query?: DictionaryParams): Observable<D> {
    const dictionary$ = definition.get(this);

    return dictionary$.pipe(map$((dictionary) =>
      definition.filter ? definition.filter(dictionary, query) : dictionary));
  }

  downloadGlobalDictionary(): Observable<GlobalDictionaryDto> {
    return this.dictionaryDataService.getGlobal().pipe(map$((result) => {
      this.setGlobal(result);
      return result;
    }));
  }

  getEnumByDictionary(name: string, dictionary: DictionaryItemDto[]): Enum<any> {
    const def = {};
    dictionary.forEach((item) => {
      def[item.localization] = item.itemCode;
    });
    return createEnum(name, def);
  }

  getTextDictionaryFromGlobal(code: string): Observable<TextDictionary> {
    return this.getFromGlobal(code, (dict: DictionaryItemDto[]) => new TextDictionary(dict.map(dictionaryItemFromDto)));
  }

  getDictionaryFromGlobal(code: string): Observable<DictionaryItemDto[]> {
    return this.getFromGlobal(code, (dict: DictionaryItemDto[]) => dict);
  }

  private getRawData(name: string): Observable<DictionaryItem<string>[]> {
    const values: Maybe<string[]> = this.findRawData(name);
    if (isNil(values)) {
      return throwError$(`Translation for dictionary '${name}' are not defined`);
    }

    return of$(values.map((value) => ({
      id: value,
      name: this.translationService.get(`dictionary:${name}:${value}`),
    })));
  }

  /**
   * Tries to find raw data using regional and non-regional selector in order.
   * @param {string} name
   * @returns {Maybe<string[]>}
   */
  private findRawData(name: string): Maybe<string[]> {
    const regionalRawData = this.translationService.getRaw(`dictionary:${name}{${REGION_CODE}}`);

    if (regionalRawData) {
      return regionalRawData;
    }

    return this.translationService.getRaw(`dictionary:${name}`);
  }

  private setGlobal(dictionaryList: any): void {
    const currentTime = new Date().getTime();
    const newGlobalDictionary = {
      time: currentTime + this.cacheTime,
      dictionaries: dictionaryList,
      locale: this.userContext.data.locale.code,
    };
    sessionStorage.setItem('globalDictionary', JSON.stringify(newGlobalDictionary));
    this.onSubject.next('globalDictionary');
    setTimeout(() => this.onSubject.next('globalDictionary'), 100);
    this.isDictionaryLoading = false;
  }

  private getFromGlobal(code: string, callback: (value: unknown) => unknown): any {
    const currentTime = new Date().getTime();
    const storageDictionary = sessionStorage.getItem('globalDictionary');
    const locale = this.userContext.data.locale.code;

    if (this.isDictionaryLoading) {
      return this.watchStorage().pipe(
        filter$((changes) => changes === 'globalDictionary'),
        map$(() => {
          const storage = sessionStorage.getItem('globalDictionary');
          const dict = storage ? JSON.parse(storage) as GlobalDictionary : null;
          return callback(dict?.dictionaries[code]);
        }),
        first$()
      );
    }

    const dictionary = storageDictionary ? JSON.parse(storageDictionary) as GlobalDictionary : null;
    if (!dictionary || dictionary.time < currentTime || dictionary.locale !== locale
      || this.isMissingFromCachedGlobal(dictionary, code)) {
      this.isDictionaryLoading = true;
      return this.downloadGlobalDictionary().pipe(
        catchError$((error) => {
          this.isDictionaryLoading = false;
          return throwError$(error);
        }),
        switchMap$((result) => result && result[code]
          ? of$(callback(result[code]))
          : throwError$(`Enum is not defined for code ${code}`)));
    }
    if (dictionary && dictionary.dictionaries && dictionary.dictionaries[code]) {
      const targetDictionary = dictionary.dictionaries[code] as DictionaryItemDto[];
      return of$(callback(targetDictionary));
    }
    return throwError$(`Enum is not defined for code ${code}`);
  }

  /**
   * A session cache saved before a global dictionary was added (e.g. JOB_NOT_PROCEEDING_REASON) lacks that code.
   * Refetch once per code. Codes outside globalDictionaries (TEMPLATES, TIMEZONES, ...) never come from the
   * global payload: they fall through to getSeparateDictionary.
   */
  private isMissingFromCachedGlobal(dictionary: GlobalDictionary, code: string): boolean {
    const isGlobalCode = Object.keys(globalDictionaries).some((key) => globalDictionaries[key] === code);
    if (!isGlobalCode || dictionary.dictionaries?.[code] || this.refetchedMissingCodes.has(code)) {
      return false;
    }
    this.refetchedMissingCodes.add(code);
    return true;
  }

  private getSeparateDictionary(code: string): Observable<TextDictionary> {
    return <Observable<TextDictionary>>this.dictionaryContext.get(code).pipe(
      catchError$(() => this.getRawData(code).pipe(
        map$((items: DictionaryItem<any>[]) => new TextDictionary(items)),
        tap$((dictionary: Dictionary<any>) => this.dictionaryContext.put(code, dictionary)))),
      catchError$(() => this.dictionaryDataService
        .get(code).pipe(
          map$((items: DictionaryItemDto[]) => new TextDictionary(items.map(dictionaryItemFromDto))),
          tap$((dictionary: Dictionary<any>) => this.dictionaryContext.put(code, dictionary)))));
  }

  private watchStorage(): Observable<any> {
    return this.onSubject.asObservable();
  }
}
